// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from 'swiper/react';
import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import Dots from '@src/components/Dots';
import NLink from '@src/components/NLink';
import IconLink from '@src/components/IconLink';
import ButtonLeftArrow from '@src/components/Button/set/ButtonLeftArrow';
import ButtonRightArrow from '@src/components/Button/set/ButtonRightArrow';

import { useSliderHooks } from './hooks';
import css from './style.module.scss';
import ButtonLeftArrowBig from '../Button/set/ButtonLeftArrowBig';
import ButtonRightArrowBig from '../Button/set/ButtonRightArrowBig';

const SwipeSlider = React.forwardRef((props, sliderApi) => {
	const {
		children,
		mix,
		adaptive,
		dotsType,
		slideMix,
		trackMix,
		centred,
		isWithDots,
		arrowsPos,
		arrowMix,
		wrapMix,
		isInnerArrows,
		title,
		moreHref,
		moreLabel,
		topNode,
		isMoreIconForced,
		moreTarget,
		swipeQtty = 'auto',
		headMix,
		centerInsufficientSlides,
		centeredSlides,
		LeftButton,
		RightButton,
		state,
		titleMix,
		isOnlyWrap,
		isBigArrows,
		wrappedNode,
		onClickMore,
		titleName,
		titleHref,
	} = props;

	const {
		screenCount,
		activeScreen,
		activeIndex,
		isEnd,
		handleSwiper,
		handleDotSwipe,
		slideLeft,
		slideRight,
	} = useSliderHooks({ sliderApi });

	const slides = React.useMemo(
		() => React.Children.toArray(children),
		[children],
	);

	let LeftArrow = LeftButton || ButtonLeftArrow;
	let RightArrow = RightButton || ButtonRightArrow;

	if (isBigArrows) {
		LeftArrow = ButtonLeftArrowBig;
		RightArrow = ButtonRightArrowBig;
	}

	const arrowsNode =
		screenCount > 1 && !isOnlyWrap ? (
			<>
				<LeftArrow
					onClick={slideLeft}
					disabled={activeIndex <= 0 && !isEnd}
					mods={cn(css.arrow, css.arrow_left, {
						[css.arrow_big]: isBigArrows,
						[css.arrow_disabled]: activeIndex <= 0 && !isEnd,
					})}
				/>
				<RightArrow
					onClick={slideRight}
					disabled={isEnd}
					mods={cn(css.arrow, css.arrow_right, {
						[css.arrow_big]: isBigArrows,
						[css.arrow_disabled]: isEnd,
					})}
				/>
			</>
		) : null;

	return (
		<div
			className={cn(css.block, mix, {
				[css[`block_adapt_${adaptive}`]]: !!adaptive,
				[css[`block_state_${state}`]]: !!state,
			})}
		>
			{title && (
				<div className={cn(css.head, headMix)}>
					<div className={cn(css.title, titleMix)}>
						{title}{' '}
						{titleHref && (
							<NLink href={titleHref}>
								<a className={css.headLink}>{titleName}</a>
							</NLink>
						)}
					</div>
					<div className={css.headUI}>
						{moreLabel && (
							<div className={css.more}>
								<NLink href={moreHref}>
									<IconLink
										onClick={onClickMore}
										rightIcon="rightArrow"
										type="thin"
										target={moreTarget}
										href={moreHref}
										className={css.moreLink}
										iconMix={cn(css.moreIcon, {
											[css.moreIcon_forced]:
												isMoreIconForced,
										})}
									>
										{moreLabel}
									</IconLink>
								</NLink>
							</div>
						)}
						{!isInnerArrows && arrowsNode && (
							<div
								className={cn(
									css.headArrows,
									css.headArrows_nocoarse,
								)}
							>
								{arrowsNode}
							</div>
						)}
					</div>
				</div>
			)}
			{topNode}
			<div className={cn(css.wrap, wrapMix)}>
				{isInnerArrows && !isOnlyWrap && (
					<div
						className={cn(
							css.arrowHolder,
							arrowMix,
							css.arrowHolder_nocoarse,
							{
								[css[`arrowHolder_pos_${arrowsPos}`]]:
									arrowsPos,
							},
						)}
					>
						{arrowsNode}
					</div>
				)}
				{isOnlyWrap && wrappedNode}
				{!isOnlyWrap && (
					<Swiper
						cssMode
						slidesPerView={swipeQtty}
						spaceBetween={0}
						className={cn(css.track, trackMix, {
							[css.track_centred]: centred && screenCount === 1,
						})}
						simulateTouch={false}
						onSwiper={handleSwiper}
						centerInsufficientSlides={centerInsufficientSlides}
						centeredSlides={centeredSlides}
					>
						{slides.map((content, i) => {
							return (
								<SwiperSlide
									key={content.key}
									className={cn(css.slide, slideMix, {
										[css.slide_last]:
											i + 1 === slides.length,
									})}
								>
									{content}
								</SwiperSlide>
							);
						})}
					</Swiper>
				)}
				{isWithDots && (
					<div
						className={cn(css.dots, {
							[css.dots_inactive]: screenCount <= 1,
						})}
					>
						<Dots
							state={dotsType}
							current={activeScreen}
							total={screenCount > 1 ? screenCount : 0}
							onChange={handleDotSwipe}
						/>
					</div>
				)}
			</div>
		</div>
	);
});

SwipeSlider.displayName = 'SwipeSlider';

SwipeSlider.propTypes = {
	isMoreIconForced: PropTypes.bool,
	title: PropTypes.string,
	moreHref: PropTypes.string,
	moreLabel: PropTypes.string,
	mix: PropTypes.string,
	adaptive: PropTypes.oneOf(['mapLayer', 'profileMore', 'listing']),
	centred: PropTypes.bool,
	isWithDots: PropTypes.bool,
	dotsType: Dots.type.propTypes.state,
	slideMix: PropTypes.string,
	trackMix: PropTypes.string,
	arrowMix: PropTypes.string,
	isInnerArrows: PropTypes.bool,
	children: PropTypes.oneOfType([
		PropTypes.arrayOf(PropTypes.node),
		PropTypes.node,
	]),
	arrowsPos: PropTypes.oneOf(['center']),
	topNode: PropTypes.element,
	wrapMix: PropTypes.string,
	moreTarget: PropTypes.string,
	headMix: PropTypes.string,
	centerInsufficientSlides: PropTypes.bool,
	centeredSlides: PropTypes.bool,
	swipeQtty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	LeftButton: PropTypes.elementType,
	RightButton: PropTypes.elementType,
	state: PropTypes.oneOf(['small-title']),
	titleMix: PropTypes.string,
	isOnlyWrap: PropTypes.bool,
	wrappedNode: PropTypes.node,
	onClickMore: PropTypes.func,
	titleName: PropTypes.string,
	titleHref: PropTypes.string,
	isBigArrows: PropTypes.bool,
};

SwipeSlider.defaultProps = {
	mix: undefined,
	centred: false,
	isWithDots: false,
	isInnerArrows: true,
	dotsType: undefined,
	adaptive: undefined,
	children: undefined,
	slideMix: undefined,
	trackMix: undefined,
	arrowsPos: undefined,
	arrowMix: undefined,
	title: undefined,
	moreHref: undefined,
	moreLabel: undefined,
	centerInsufficientSlides: undefined,
	topNode: undefined,
	headMix: undefined,
	isMoreIconForced: false,
	wrapMix: undefined,
	LeftButton: undefined,
	RightButton: undefined,
	centeredSlides: undefined,
	titleMix: undefined,
	moreTarget: '_self',
	swipeQtty: 'auto',
	state: undefined,
	isOnlyWrap: false,
	wrappedNode: null,
	titleName: undefined,
	titleHref: undefined,
	onClickMore: undefined,
	isBigArrows: undefined,
};

export default SwipeSlider;
